import React from 'react';
import type { MouseEvent, ReactElement } from 'react';

import { CreditsPopup } from '@atlassian/rovo-credits/credits-popup';
import type { MenuItem } from '@atlassian/universal-create/types';

import { useIsOutOfRovoCredits } from '../../useIsOutOfRovoCredits.ts';

export type MenuItemWrapperChildren = Parameters<NonNullable<MenuItem['Wrapper']>>[0]['children'];

type AiCreditMenuItemGuardProps = {
	children: MenuItemWrapperChildren;
	/** Localized menu item label, used as the popup heading. */
	title: string;
	/** Wraps the menu item when credits are available; defaults to the plain menu item. */
	renderAllowed?: (children: MenuItemWrapperChildren) => ReactElement;
};

/**
 * Universal Create menu item `Wrapper` that anchors the out-of-credits popup to the menu item.
 * The popup's trigger props replace the menu item's click handler, which keeps the menu open.
 */
export const AiCreditMenuItemGuard = ({
	children,
	title,
	renderAllowed,
}: AiCreditMenuItemGuardProps): ReactElement => {
	const isOutOfCredits = useIsOutOfRovoCredits();

	if (isOutOfCredits) {
		return (
			<CreditsPopup
				title={title}
				placement="right-start"
				offset={[0, 16]}
				shouldRenderToParent={false}
				trigger={({ onClick, ...popupTriggerProps }) => {
					const triggerProps = {
						...popupTriggerProps,
						onClick: (e: MouseEvent) => {
							e.preventDefault();
							onClick();
						},
					};
					return children({ triggerProps });
				}}
			/>
		);
	}

	return renderAllowed ? renderAllowed(children) : <>{children()}</>;
};
